// React core imports
import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'

// Third-party libraries
import groq from 'groq'

// Context hooks
import { useModalContext } from '@/context/ModalContext'
import { useCustomerContext } from '@/context/CustomerContext'
import { useHeadlessCheckoutContext } from '@/context/HeadlessCheckoutContext'
import { usePurchaseFlowContext } from '@/context/PurchaseFlowContext'

// Section components
import ContentSections from '@/components/Sections/ContentSections'
import ProductContentSections from '@/components/Sections/ProductContentSections'
import ComparisonChart from '@/components/Sections/ComparisonChart'
import SustainabilityAndCommunity from '@/components/SustainabilityAndCommunity'

// Product components
import ProductSlider from '../../components/Product/ProductSlider'
import ProductForm from '@/components/Product/ProductForm'
import ProductGiftSubForm from '@/components/Product/ProductGiftSubForm'
import ProductEarlybirdForm from '@/components/Product/ProductEarlybirdForm'
import ProductPricing from '@/components/Product/ProductPricing'

// SEO components
import PageSEO from '@/components/SEO/PageSEO'
import StructuredData from '@/components/SEO/StructuredData'

// Review components
import ReviewStarsWithCounter from '@/components/Review/ReviewStarsWithCounter/ReviewStarsWithCounter'

// Preview components
import { PreviewButton } from '@/components/Preview/PreviewButton'

// Services
import { getSanityClient } from '@/services/sanityClient'

// Utility functions
import { getRecentArticlesHandles } from '@/utils/getRecentArticleHandles'
import { dataLayerViewProduct, dataLayerPDPAddToBasketClick } from '@/utils/dataLayer'
import { getMetafield } from '@/utils/getMetafield'
import { getHarvests } from '@/utils/getHarvests'
import { getProductsByHandles } from '@/utils/getProducts'
import { getContentGatingModal, userCanAccessContent } from '@/utils/gatingUtils'
import { getGatedProductModal } from '@/utils/getGatedProductModal'
import { getInventory } from '@/utils/getInventory'
import { getAllProducts } from '@/utils/getProducts'
import { getBoxDetailByHandles } from '@/utils/getBoxDetailByHandles'
import { getProductContentByHandle } from '@/utils/getProductContentByHandle'
import { getDefaultPurchaseOption } from '@/utils/getDefaultPurchaseOption'
import { getSellingPlanGroupsByProductId } from '@/utils/sellingPlans'
import getCollectionListProducts from '@/utils/getCollectionListProducts'

// Custom hooks
import useProductReviewsDetails from '@/hooks/useProductReviewsDetails'

// Styles
import classes from './Product.module.scss'

// Dynamic imports
const ProductDetailsList = dynamic(() => import('@/components/Product/ProductDetailsList'), { ssr: false })
const PressLogos = dynamic(() => import('@/components/Sections/PressLogos'))

function Product({
  isGift,
  isSubscription,
  isGiftCard,
  isEarlyBirdPrepaid,
  // defaultProduct,
  products,
  page,
  modals,
  preview,
  previewToken,
  defaultPurchaseOption,
  whyBuyFromUs,
  sustainabilityAndCommunityQuery,
  footerSettings,
  productHandle,
}) {
  // get the default product from the products array 
  let defaultProduct = products[0]

  if (isSubscription && isGift) {
    // isGift can be removed if we want subscription pages to have all subscription options
    // It would be nice to have a better way to do this, but I can't think of one for right now.
    const modifiedHandle = productHandle
      .replaceAll('gift', '')
      .replaceAll('subscription', '')
      .replaceAll('box', '')
      .replaceAll('-', '')
    const matchingProduct = products.find((p) => {
      const modifiedProductHandle = p.content?.handle
        .replaceAll('gift', '')
        .replaceAll('subscription', '')
        .replaceAll('box', '')
        .replaceAll('-', '')
      return modifiedProductHandle === modifiedHandle
    })

    if (matchingProduct) defaultProduct = matchingProduct
  }


  const { data, addItemToOrder } = useHeadlessCheckoutContext()
  const purchaseFlowContext = usePurchaseFlowContext()
  const [product, setProduct] = useState(defaultProduct)
  const [giftOptionChecked, setGiftOptionChecked] = useState(isGift)
  const router = useRouter()
  const { variant: urlVariant } = router.query
  // Helper to match the slug produced in ChooseYourBox (lowercased title with spaces -> dashes)
  // Slugify full variant title to align with ChooseYourBox navigation
  const variantSlug = (v) =>
    v?.content?.title
      ? v.content?.title?.toLowerCase().replace(/\s+/g, '-')
      : ''

  const [selectedVariant, setSelectedVariant] = useState(() => {
    if (isSubscription && urlVariant && product.variants) {
      const variantFromUrl = product.variants.find(
        (v) => variantSlug(v) === urlVariant?.toLowerCase(),
      )
      return variantFromUrl || product.variants[0]
    }
    return product.variants[0]
  })
  const handle = product.content?.handle
  const reviewInfo = useProductReviewsDetails(handle)
  const harvests = product.harvests
  const reviewsRef = useRef(null);

  const variants = useMemo(() => {
    return product.variants
  }, [product.variants])

  const productAccordionHeaders = page?.fields?.content?.find(
    (block) => block._type === 'productAccordionHeaders',
  )

  const productHasPurchaseOptions = // try to keep things from breaking if not totally set up right
    !!page?.fields.purchaseOptions?.oneTimePurchase &&
    !!page?.fields.purchaseOptions?.subscription
  const productPurchaseOptions = productHasPurchaseOptions
    ? page.fields.purchaseOptions
    : defaultPurchaseOption

  const accordionDeliveryHeader = productAccordionHeaders?.details
  const productDescription = product.content?.description
  const accordionDescriptionHeader = productAccordionHeaders?.description
  const deliveryDetails = getMetafield({ product, key: 'delivery_details' })
  const deliveryDetailsList = deliveryDetails
    ? JSON.parse(deliveryDetails.value)
    : null

  const modalContext = useModalContext()
  const customerContext = useCustomerContext()
  const { customer } = customerContext

  const onSelectProduct = (e) => {
    const product = products.find(
      (product) => product.content?.handle === e.value,
    )
    setProduct(product)
    setSelectedVariant(product.variants[0])
  }

  const pressLogos = page?.fields.content?.find(
    (block) => block._type === 'pressLogos',
  )

  const [frequency, setFrequency] = useState(
    purchaseFlowContext.options.frequency || 'monthly'
  )

  const addToCart = useCallback(async () => {
    dataLayerPDPAddToBasketClick({
      product,
      variant: selectedVariant,
      quantity: 1,
      url: router.asPath,
      context: 'pdp_add_to_basket',
    })
    await addItemToOrder({
      product,
      variant: selectedVariant,
      properties: isSubscription ? {
        interval_text: frequency.toString(),
        membership_type: 'subscription',
      } : {},
      openFlyout: true,
    });
  }, [product, selectedVariant, addItemToOrder, isSubscription, frequency, router.asPath]);

  const handleBuyNow = useCallback(async () => {
    await addItemToOrder({
      product,
      variant: selectedVariant,
      properties: isSubscription ? {
        interval_text: frequency.toString(),
        membership_type: 'subscription',
      } : {},
      redirectToCart: true,
    });
    router.push('/cart');
  }, [addItemToOrder, product, selectedVariant, router, isSubscription, frequency]);

  // Keep selectedVariant in sync with `?variant=<slug>` from ChooseYourBox
  useEffect(() => {
    setFrequency(purchaseFlowContext.options.frequency || 'monthly')
  }, [purchaseFlowContext.options.frequency])

  useEffect(() => {
    async function loadInventoryAndSellingPlans() {
      const inventory = await getInventory([
        { id: product.variants[0].sourceEntryId, quantity: 1 },
      ])
      const sellingPlans = await getSellingPlanGroupsByProductId(product.id)
      setProduct({
        ...product,
        sellingPlans,
        variants: product.variants.map((v) => ({ ...v, sellingPlans })),
      })
      setSelectedVariant({
        ...selectedVariant,
        availableForSale: !inventory.inventoryIssues,
        sellingPlans,
      })
    }
    loadInventoryAndSellingPlans()
  }, [])

  useEffect(() => {
    setProduct(defaultProduct)
    setSelectedVariant(defaultProduct?.variants?.[0])
  }, [defaultProduct?.handle])

  useEffect(() => {
    setGiftOptionChecked(data?.cart?.properties?.is_gift_order === 'true')
  }, [data?.cart?.properties?.is_gift_order])

  // GATING
  useEffect(() => {
    if (isGiftCard) {
      // why is this in this use effect? -- ZZJ
      setGiftOptionChecked(true)
    }

    const customerHasAccess = userCanAccessContent({
      contentTags: product.tags,
      userTags: customer?.tags,
    })

    if (!customerHasAccess) {
      const foundModal = getContentGatingModal({
        modals,
        contentTags: product.tags,
      })
      if (foundModal) {
        modalContext.setContent(foundModal.fields)
        modalContext.setModalType('gated_product')
        modalContext.setIsOpen(true)
      }
    }

    if (modalContext.modalType === 'gated_product' && customerHasAccess) {
      modalContext.setIsOpen(false)
    }
  }, [customer, modalContext.isOpen])

  useEffect(() => {
    dataLayerViewProduct({ product })
  }, [])

  const handleCheckbox = () => {
    setGiftOptionChecked(!giftOptionChecked)
  }

  const availableForSale = selectedVariant?.availableForSale

  useEffect(() => {
    if (isSubscription && urlVariant && product.variants) {
      const variantFromUrl = product.variants.find(
        (v) => variantSlug(v) === urlVariant?.toLowerCase(),
      )
      if (variantFromUrl) {
        setSelectedVariant(variantFromUrl)
      }
    }
  }, [urlVariant, product.variants, isSubscription])

  if (page && product && !isGift && !isGiftCard && !isEarlyBirdPrepaid) {
    const sections =
      urlVariant && page.fields.variantUrlKey === urlVariant
        ? page.fields.variantContent
        : page.fields.content
    return (
      <>
        <PageSEO
          seo={page?.fields?.seo}
          product={product}
          images={[
            {
              url: product.content?.media?.[0]?.src,
              alt: product.content?.title,
            },
          ]}
        />
        <StructuredData type="product" data={product} />
        {preview && previewToken && <PreviewButton />}
        <ProductContentSections
          sections={sections}
          product={{
            ...product,
            imagesFromSanity: page?.fields.productImages,
            pricingDisplay: page?.fields.pricingDisplay,
          }}
          variant={selectedVariant}
          setSelectedVariant={setSelectedVariant}
          productPurchaseOptions={productPurchaseOptions}
          reviewsRef={reviewsRef}
          whyBuyFromUs={whyBuyFromUs}
          isSubscription={isSubscription}
          footerSettings={footerSettings}
        />
        {isSubscription && pressLogos && (
          <PressLogos fields={pressLogos} isSubscription={isSubscription} />
        )}
        {isSubscription && (
          <ComparisonChart
            logo={footerSettings?.logo}
          />
        )}
        {sustainabilityAndCommunityQuery.length > 0 && (
          <SustainabilityAndCommunity
            sections={sustainabilityAndCommunityQuery}
          />
        )}
        {isSubscription && (
          <div className={classes['product-hero-buttons']}>
            {' '}
            <button
              className={classes['add-to-basket-btn']}
              disabled={!availableForSale}
              onClick={addToCart}
            >
              {!availableForSale ? 'Sold Out' : 'Add to Basket'}
            </button>
            <button
              className={`${classes['add-to-basket-btn']} ${classes['buy-now-btn']}`}
              disabled={!availableForSale}
              onClick={handleBuyNow}
            >
              Buy Now
            </button>
          </div>
        )}
      </>
    )
  }

  // selectedVariant drives PDP add-to-cart and buy-now actions

  return (
    product && (
      <div
        className={`${classes['product']} ${
          isGift ? classes['gift-subscription-product'] : ''
        } product `}
      >
        {preview && previewToken && <PreviewButton />}
        <StructuredData type="product" data={product} />
        {page?.fields.seo && (
          <PageSEO product={product} seo={page?.fields.seo} />
        )}
        <div className={classes['product__inner']}>
          <div className={`${classes['product__row']} container`}>
            <div className={classes['slider']}>
              <ProductSlider product={product} />
            </div>

            <div className={classes['main']}>
              {reviewInfo && (
                <ReviewStarsWithCounter
                  numberOfReviews={reviewInfo.totalReviews}
                  rating={reviewInfo.average}
                />
              )}

              {product.content?.title && (
                <h1 className={classes['product-title']}>
                  {product.content?.title}
                </h1>
              )}

              {!isGiftCard && !isEarlyBirdPrepaid && (
                <div className={classes['prices']}>
                  <ProductPricing
                    product={product}
                    selectedVariant={selectedVariant}
                    large
                  />
                </div>
              )}

              {!isGift && !isEarlyBirdPrepaid && (
                <div className={classes['gift']}>
                  {!isGiftCard && (
                    <div className={classes['gift__check']}>
                      <input
                        id="giftCheck"
                        type="checkbox"
                        checked={giftOptionChecked}
                        onChange={handleCheckbox}
                      />
                      <label htmlFor="giftCheck" className="heading--label">
                        This is a Gift
                      </label>
                    </div>
                  )}
                </div>
              )}

              {/* PRODUCT FORM  */}
              {product ? (
                isSubscription && isGift ? (
                  <ProductGiftSubForm
                    selectedVariant={selectedVariant}
                    setSelectedVariant={setSelectedVariant}
                    allProducts={products}
                    product={product}
                    onSelectProduct={onSelectProduct}
                  />
                ) : isEarlyBirdPrepaid ? (
                  <ProductEarlybirdForm
                    selectedVariant={selectedVariant}
                    setSelectedVariant={setSelectedVariant}
                    allProducts={products}
                    product={product}
                    onSelectProduct={onSelectProduct}
                  />
                ) : (
                  <ProductForm
                    giftOptionChecked={giftOptionChecked}
                    handle={handle}
                    product={product}
                    variants={variants}
                    setSelectedVariant={setSelectedVariant}
                    selectedVariant={selectedVariant}
                  />
                )
              ) : null}

              {/* ACCORDION */}
              {deliveryDetailsList && (
                <div className={`product-accordions ${classes['accordion']}`}>
                  <ProductDetailsList
                    fields={{
                      detailsTitle: accordionDescriptionHeader,
                      description: productDescription ? productDescription : '',
                    }}
                    enableToggle={true}
                    expandOnLoad={true}
                  />
                  <ProductDetailsList
                    fields={{
                      detailsTitle: accordionDeliveryHeader,
                      detailsItems: deliveryDetailsList,
                    }}
                    enableToggle={true}
                  />
                </div>
              )}

              {/* {!!badgeFields && (
                <ProductStamps fields={badgeFields} product={product} />
              )} */}
            </div>
          </div>

          {/* SECTIONS */}
          {!isEarlyBirdPrepaid && (
            <ContentSections
              product={product}
              sections={page?.fields.content}
              harvests={
                harvests &&
                harvests
                  .filter(
                    (harvest) =>
                      harvest.variantTitle === selectedVariant.content?.title,
                  )
                  .map((harvest) => {
                    return {
                      ...harvest,
                      header: harvest.variantTitle,
                    }
                  })
              }
              disableHarvestFilters={true}
            />
          )}
        </div>
      </div>
    )
  )
}

export default Product

export async function getStaticPaths() {
  const products = await getAllProducts()
  const handles = products
    .filter((p) => buildProductPage(p))
    .map(({ handle }) => ({ params: { handle: handle } }))
  return {
    paths: handles,
    fallback: 'blocking',
  }
}

function buildProductPage(product) {
  return !(
    product.tags?.includes('Swap') ||
    product.tags.map((t) => t.toLowerCase())?.includes('algolia-ignore')
  )
}

const whyBuyFromUsQuery = groq`
*[_type == "whyBuyFromUs"] {
  ...,
}
`
const sustainabilityAndCommunityQuery = groq`
*[_type == "sustainabilityAndCommunity"] {
  ...,
  sections[]{
    ...,
    fullBleedHero {
      ...,
      desktopBackgroundImage{
        ...,
        asset->{...}
      },
      mobileBackgroundImage{
        ...,
        asset->{...}
      },
    },
    image {
      ...,
      asset->{...}
    },
    imageTwo {
      ...,
      asset->{...}
    },
    imageThree {
      ...,
      asset->{...}
    }
  },
}
`
export async function getStaticProps({ params, draftMode }) {
  const handle = params.handle
  const isGift = handle?.includes('gift')
  const isSubscription = handle?.includes('subscription')

  console.log('isSubscription', isSubscription)
  console.log('handle', handle)

  const isGiftCard = handle === 'digital-gift-card'
  const isEarlyBirdPrepaid = handle === 'early-bird-prepaid'
  const isIntroBox = handle === 'sitka-seafood-intro-box'
  const isSustainerProduct = handle === 'sustainers'
  const gatedProductModal = await getGatedProductModal()

  let productHandles = []
  if (isSubscription && isGift) {
    // isGift can be removed if we want subscription pages to have all subscription options
    productHandles.push(
      'premium-seafood-subscription-box',
      'seafood-subscription-box',
      'salmon-subscription-box',
    )
  } else {
    productHandles.push(handle)
  }

  const preview = draftMode || false
  const previewToken = preview ? process.env.SANITY_READ_TOKEN : ``
  if (preview && !previewToken) {
    throw new Error(
      `Preview mode is active, but SANITY_READ_TOKEN is not set in environment variables`,
    )
  }

  let products = await getProductsByHandles({ productHandles })
  products = products.filter((p) => buildProductPage(p))

  if (!products.length) {
    return {
      notFound: true,
    }
  }

  let boxDetails = null;

  if (isSubscription) {
    // Get boxDetails for all subscription products
    const boxDetailsArray = await getBoxDetailByHandles(productHandles)

    // Get the first item since we're only dealing with one product
    boxDetails = boxDetailsArray?.[0] || null

    // If it's a gift subscription, also add the box info to products
    if (isGift) {
      const productsWithBoxInfo = products.map((product) => {
        const boxInfo = boxDetailsArray
          ? boxDetailsArray.find((box) => box.handle === product.content?.handle)
          : {}
        return {
          ...product,
          boxDetails: boxInfo,
        }
      })
      products = productsWithBoxInfo
    }
  }

  for (const product of products) {
    const harvestData = await getHarvests({ product })
    product.harvests = harvestData
  }

  let pageHandle = handle
  if (isGift && isSubscription) pageHandle = 'gift-subscription-product'

  const sanityClient = getSanityClient(previewToken)
  let page = await getProductContentByHandle(pageHandle, sanityClient)
  const { purchaseOptions: defaultPurchaseOption } =
    await getDefaultPurchaseOption(sanityClient)

  if (
    page?.fields?.content?.some(
      (content) => content?._type === 'featuredBlogContent',
    )
  ) {
    await getRecentArticlesHandles(fullRefPage.fields.content)
  }

  const whyBuyFromUs = await sanityClient.fetch(whyBuyFromUsQuery)
  const sustainabilityAndCommunity = await sanityClient.fetch(sustainabilityAndCommunityQuery)

  if (
    !products.length ||
    ((!page || !page?.fields?.content) &&
      !isSubscription &&
      !isGift &&
      !isGiftCard &&
      !isEarlyBirdPrepaid &&
      !isIntroBox &&
      !isSustainerProduct)
  ) {
    return {
      notFound: true,
    }
  }

  if (!page) page = await getProductContentByHandle('product', sanityClient)

  if (
    page?.fields?.content?.some(
      (content) => content?._type === 'featuredBlogContent',
    )
  ) {
    await getRecentArticlesHandles(page.fields.content)
  }

  if (
    page?.fields?.content?.some((content) => content?._type === 'collectionList')
  ) {
    await getCollectionListProducts(page)
  }

  if (
    page?.fields?.content?.some((content) => content?._type === 'collectionHero')
  ) {
    await getCollectionHeroProducts(page)
  }

  if (
    page?.fields?.content?.some(
      (content) => content?._type === 'featuredSubscriptionBlock',
    )
  ) {
    await getFeaturedSubscriptionProducts(page)
  }

  let defaultProduct = products[0]
  const seoHiddenField = defaultProduct.metafields.find(
    (mf) => mf.key === 'hidden' && mf.namespace === 'seo',
  )

  if (seoHiddenField?.value === '1') {
    return {
      notFound: true,
    }
  }

  const footerSettingsQuery = `*[handle.current == "footer-settings"][0]`
  const footerSettings = await sanityClient.fetch(footerSettingsQuery)

  const props = {
    productHandle: handle,
    products,
    defaultPurchaseOption,
    page: page ? page : null,
    modals: gatedProductModal,
    isGift,
    isSubscription,
    isGiftCard,
    isEarlyBirdPrepaid,
    isIntroBox,
    isSustainerProduct,
    preview,
    previewToken,
    whyBuyFromUs: whyBuyFromUs,
    sustainabilityAndCommunityQuery: sustainabilityAndCommunity,
    boxDetails: boxDetails,
    footerSettings: footerSettings || null,
  }

  return {
    props,
  }
}
